import PropTypes from 'prop-types';
import Layout from '@components/Layout/Layout';
import { getBoltPost } from '@helpers/posts';
import { skipStaticBuild } from '@helpers/utils';
import { toReadableDate } from '@hiredigital/lib/helpers/marketingDate';
import BlogDetail from '@components/Blog/BlogDetail';
import Header from '@components/Header/InsightsHeader';

const BoltPostTemplate = ({
  head,
  title,
  description,
  featuredImage,
  postUrl,
  categories,
  author,
  modified,
  content,
  tags,
  apiAuthor,
  date,
  postSchema,
  post,
  ...props
}) => {
  return (
    <Layout
      header={Header}
      isPost
      pageHead={head}
      postSchema={postSchema}
      variant={Layout.Variants.INSIGHTS}
      desktopMatType={Layout.desktopMatType.FULL_PAGE_MODAL}>
      <BlogDetail
        postUrl={postUrl}
        apiAuthor={apiAuthor}
        author={author}
        title={title}
        date={date}
        modified={modified}
        featuredImage={featuredImage}
        categories={categories}
        content={content}
        tags={tags}
        showBreadcrumbs={false}
      />
    </Layout>
  );
};

BoltPostTemplate.propTypes = {
  head: PropTypes.object,
  postUrl: PropTypes.string,
  content: PropTypes.string,
  featuredImage: PropTypes.object,
  title: PropTypes.string,
  tags: PropTypes.object,
  categories: PropTypes.object,
  excerpt: PropTypes.string,
};

export default BoltPostTemplate;

export const getStaticPaths = async () => {
  if (skipStaticBuild) return { paths: [], fallback: 'blocking' };

  // TODO add paths for static generation
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ params }) => {
  const { category, slug } = params;
  try {
    const post = await getBoltPost(slug);
    if (!params.slug || !post) {
      return {
        notFound: true,
        revalidate: 7200,
      };
    }

    let {
      modified,
      date,
      title,
      seo: yoast,
      excerpt,
      tags,
      blogPostMeta,
      author,
      featuredImage,
      categories,
    } = post;

    const postUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/bolt/${category}/${post.slug}`;
    const description = post.excerpt.replace(/(<([^>]+)>)/g, '');
    const stringTags = post.tags.nodes.map((n) => n.name).join(', ');

    const postSchema = {
      author: author?.node?.name || 'Hire Digital',
      datePublished: yoast?.opengraphPublishedTime || date,
      publisher: 'Hire Digital',
      dateModified: yoast?.opengraphModifiedTime || modified,
    };

    const pageHead = {
      title: `${post.title} | Hire Digital`,
      meta: [
        { name: 'title', content: yoast?.title || post.title },
        {
          name: 'description',
          content: yoast?.metaDesc || description,
        },
        {
          name: 'keywords',
          content: yoast?.metaKeywords || stringTags,
        },
        { name: 'author', content: post.author?.node?.name },
        {
          property: 'og:title',
          content: yoast?.opengraphTitle || post.title,
        },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: postUrl },
        {
          property: 'og:image',
          content: yoast?.opengraphImage?.sourceUrl || post.featuredImage?.node?.sourceUrl,
        },
        {
          name: 'image',
          content: yoast?.opengraphImage?.sourceUrl || post.featuredImage?.node?.sourceUrl,
        },
        {
          property: 'og:description',
          content: yoast?.opengraphDescription || description,
        },
        { property: 'og:publisher', content: process.env.NEXT_PUBLIC_SITE_URL },
        { property: 'og:image:width', content: '1200px' },
        { property: 'og:image:height', content: '630px' },

        { property: 'article:published_time', content: yoast?.opengraphPublishedTime },
        { property: 'article:modified_time', content: yoast?.opengraphModifiedTime },
        { property: 'article:tag', content: stringTags },
        { property: 'article:section', content: post.categories?.nodes[0].name },
      ],
    };

    if (yoast && yoast.twitter_title)
      pageHead.meta.push({
        name: 'twitter:title',
        content: yoast.twitter_title,
      });

    if (yoast && yoast.twitter_description)
      pageHead.meta.push({
        name: 'twitter:description',
        content: yoast.twitter_description,
      });

    if (yoast && yoast.twitter_image)
      pageHead.meta.push({
        name: 'twitter:image',
        content: yoast.twitter_image,
      });

    if (yoast && yoast.meta_robots_adv)
      pageHead.meta.push({
        name: 'robots',
        content: yoast.meta_robots_adv,
      });

    modified = toReadableDate(yoast?.opengraphModifiedTime);
    date = toReadableDate(yoast?.opengraphPublishedTime || date);

    return {
      props: {
        ...post,
        modified,
        date,
        head: pageHead,
        postSchema,
        postUrl,
        description,
        post,
      },
      revalidate: 7200,
    };
  } catch (err) {
    console.error(err);
    return {
      props: {
        error: {
          statusCode: 404,
          message: 'Page Not Found',
        },
      },
      revalidate: 7200,
    };
  }
};
